/* ================================================================
   PRESTIGE HEADER PORT v1.0 — for Sleek 1.7 (DUBAIARABIAN)
   ----------------------------------------------------------------
   Reimplements the Prestige theme.min.js header behaviour:
     - DrawerManager  (data-action="open-drawer|close-drawer")
     - ModalManager   (data-action="open-modal|close-modal")
     - Collapsible    (data-action="toggle-collapsible")
     - StickyHeader
     - TransparentHeader (scroll detection)
     - MegaMenuHover
     - HeaderHeight   (CSS var --header-height update)
     - CurrencySelector (cart_currency cookie)
   No jQuery, no Slick, vanilla ES2017+ (Sleek runs ES2017).
   ================================================================ */
(function () {
  'use strict';

  /* ---------- helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { el && el.addEventListener(evt, fn, opts || false); }
  function dispatch(el, name, detail) {
    el.dispatchEvent(new CustomEvent(name, { bubbles: true, cancelable: true, detail: detail || {} }));
  }
  function delegate(root, evt, selector, handler) {
    root.addEventListener(evt, function (e) {
      var target = e.target.closest(selector);
      if (target && root.contains(target)) handler.call(target, e, target);
    });
  }
  function getById(id) { return document.getElementById(id); }

  /* ---------- body scroll lock ---------- */
  var scrollLockCount = 0;
  function lockScroll() {
    if (scrollLockCount === 0) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    scrollLockCount++;
  }
  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  /* ================================================================
     PAGE OVERLAY (shared between drawers and modals)
     ================================================================ */
  var PageOverlay = {
    el: null,
    init: function () {
      this.el = $('.PageOverlay');
      if (!this.el) {
        // Create lazily if missing (defensive)
        this.el = document.createElement('div');
        this.el.className = 'PageOverlay';
        document.body.insertBefore(this.el, document.body.firstChild);
      }
      on(this.el, 'click', function () {
        DrawerManager.closeAll();
        ModalManager.closeAll();
      });
    },
    show: function () { this.el && this.el.classList.add('is-visible'); },
    hide: function () { this.el && this.el.classList.remove('is-visible'); }
  };

  /* ================================================================
     DRAWER MANAGER
     ----------------------------------------------------------------
     Pattern Prestige:
       <button data-action="open-drawer" data-drawer-id="sidebar-menu">
       <section id="sidebar-menu" class="Drawer" aria-hidden="true">
         <... data-drawer-animated-left>      <- transitioned children
         <... data-drawer-animated-bottom>
       </section>
     ================================================================ */
  var DrawerManager = {
    openDrawers: [],

    init: function () {
      var self = this;

      delegate(document, 'click', '[data-action="open-drawer"]', function (e) {
        e.preventDefault();
        var id = this.getAttribute('data-drawer-id');
        if (id) self.open(id, this);
      });

      delegate(document, 'click', '[data-action="close-drawer"]', function (e) {
        e.preventDefault();
        var id = this.getAttribute('data-drawer-id');
        if (id) self.close(id);
        else self.closeTop();
      });

      // ESC closes top drawer
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) self.closeTop();
      });
    },

    open: function (id, trigger) {
      var drawer = getById(id);
      if (!drawer || this.openDrawers.indexOf(id) !== -1) return;

      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      document.body.classList.add('is-drawer-open');
      document.body.classList.add('is-drawer-open--' + id);
      PageOverlay.show();
      lockScroll();
      this.openDrawers.push(id);

      // Stagger animated children
      this._staggerIn(drawer);

      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      dispatch(drawer, 'drawer:opened', { id: id });
    },

    close: function (id) {
      var drawer = getById(id);
      if (!drawer) return;
      var idx = this.openDrawers.indexOf(id);
      if (idx === -1) return;

      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('is-open');
      document.body.classList.remove('is-drawer-open--' + id);
      this.openDrawers.splice(idx, 1);

      if (this.openDrawers.length === 0 && ModalManager.openModals.length === 0) {
        document.body.classList.remove('is-drawer-open');
        PageOverlay.hide();
        unlockScroll();
      }

      var triggers = $$('[data-action="open-drawer"][data-drawer-id="' + id + '"]');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      this._staggerOut(drawer);
      dispatch(drawer, 'drawer:closed', { id: id });
    },

    closeTop: function () {
      var top = this.openDrawers[this.openDrawers.length - 1];
      if (top) this.close(top);
    },

    closeAll: function () {
      this.openDrawers.slice().forEach(this.close.bind(this));
    },

    _staggerIn: function (drawer) {
      var items = $$('[data-drawer-animated-left], [data-drawer-animated-right], [data-drawer-animated-bottom]', drawer);
      items.forEach(function (el, i) {
        el.style.transitionDelay = (60 + i * 40) + 'ms';
        requestAnimationFrame(function () { el.classList.add('is-visible'); });
      });
    },
    _staggerOut: function (drawer) {
      var items = $$('[data-drawer-animated-left], [data-drawer-animated-right], [data-drawer-animated-bottom]', drawer);
      items.forEach(function (el) {
        el.style.transitionDelay = '0ms';
        el.classList.remove('is-visible');
      });
    }
  };

  /* ================================================================
     MODAL MANAGER (Search + future modals)
     ================================================================ */
  var ModalManager = {
    openModals: [],

    init: function () {
      var self = this;

      delegate(document, 'click', '[data-action="open-modal"]', function (e) {
        e.preventDefault();
        var id = this.getAttribute('aria-controls') || this.getAttribute('data-modal-id');
        if (id) self.open(id, this);
      });

      delegate(document, 'click', '[data-action="close-modal"]', function (e) {
        e.preventDefault();
        var modal = this.closest('.Modal');
        if (modal) self.close(modal.id);
        else self.closeTop();
      });

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) self.closeTop();
      });
    },

    open: function (id, trigger) {
      var modal = getById(id);
      if (!modal || this.openModals.indexOf(id) !== -1) return;

      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      document.body.classList.add('is-modal-open');
      PageOverlay.show();
      lockScroll();
      this.openModals.push(id);

      // Autofocus first input
      var input = $('input[autofocus], input:not([type="hidden"]):not([disabled])', modal);
      if (input) setTimeout(function () { input.focus(); }, 80);

      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      dispatch(modal, 'modal:opened', { id: id });
    },

    close: function (id) {
      var modal = getById(id);
      if (!modal) return;
      var idx = this.openModals.indexOf(id);
      if (idx === -1) return;

      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('is-open');
      this.openModals.splice(idx, 1);

      if (this.openModals.length === 0 && DrawerManager.openDrawers.length === 0) {
        document.body.classList.remove('is-modal-open');
        PageOverlay.hide();
        unlockScroll();
      }

      var triggers = $$('[data-action="open-modal"][aria-controls="' + id + '"]');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      dispatch(modal, 'modal:closed', { id: id });
    },

    closeTop: function () {
      var top = this.openModals[this.openModals.length - 1];
      if (top) this.close(top);
    },

    closeAll: function () {
      this.openModals.slice().forEach(this.close.bind(this));
    }
  };

  /* ================================================================
     COLLAPSIBLE
     ================================================================ */
  var Collapsible = {
    init: function () {
      delegate(document, 'click', '[data-action="toggle-collapsible"]', function (e) {
        e.preventDefault();
        var expanded = this.getAttribute('aria-expanded') === 'true';
        this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    }
  };

  /* ================================================================
     STICKY + TRANSPARENT HEADER
     ----------------------------------------------------------------
     - Adds .Header--sticky once user scrolls past header
     - For .Header--transparent : adds .Header--isScrolled past threshold
       (kept transparent at top, becomes opaque on scroll)
     ================================================================ */
  var HeaderScroll = {
    el: null,
    lastY: 0,
    threshold: 60,
    ticking: false,

    init: function () {
      this.el = $('header.Header');
      if (!this.el) return;
      this._onScroll = this._onScroll.bind(this);
      on(window, 'scroll', this._onScroll, { passive: true });
      this._update();
    },

    _onScroll: function () {
      if (!this.ticking) {
        var self = this;
        requestAnimationFrame(function () { self._update(); self.ticking = false; });
        this.ticking = true;
      }
    },

    _update: function () {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      var past = y > this.threshold;
      this.el.classList.toggle('Header--isScrolled', past);
      // For transparent header : un-transparent past threshold
      if (this.el.classList.contains('Header--transparent')) {
        this.el.classList.toggle('Header--transparent-scrolled', past);
      }
      this.lastY = y;
    }
  };

  /* ================================================================
     MEGA MENU HOVER (keyboard + touch support)
     ----------------------------------------------------------------
     CSS handles :hover. We add keyboard focus + escape close + the
     DA 3-col activation script lives in the section template.
     ================================================================ */
  var MegaMenuKeyboard = {
    init: function () {
      var items = $$('.Header__MainNav [aria-haspopup="true"]');
      items.forEach(function (li) {
        var trigger = li.querySelector(':scope > a');
        if (!trigger) return;
        on(trigger, 'focus', function () { li.classList.add('is-focused'); });
        on(trigger, 'blur', function () {
          // Delay to allow focus moving into the dropdown
          setTimeout(function () {
            if (!li.contains(document.activeElement)) li.classList.remove('is-focused');
          }, 50);
        });
      });
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape') items.forEach(function (li) { li.classList.remove('is-focused'); });
      });
    }
  };

  /* ================================================================
     HEADER HEIGHT CSS VAR
     ================================================================ */
  var HeaderHeight = {
    el: null,
    init: function () {
      this.el = $('header.Header');
      if (!this.el) return;
      this._update = this._update.bind(this);
      this._update();
      on(window, 'resize', this._debounce(this._update, 120));
      // Observe header for content changes (logo loaded, sub-bar visible)
      if ('ResizeObserver' in window) {
        new ResizeObserver(this._update).observe(this.el);
      }
    },
    _update: function () {
      var h = this.el.offsetHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
      // Also pick up announcement bar
      var ann = $('#shopify-section-announcement');
      if (ann) document.documentElement.style.setProperty('--announcement-bar-height', ann.offsetHeight + 'px');
    },
    _debounce: function (fn, wait) {
      var t;
      return function () { clearTimeout(t); t = setTimeout(fn, wait); };
    }
  };

  /* ================================================================
     CURRENCY SELECTOR (uses Shopify cart_currency cookie)
     ================================================================ */
  var CurrencySelector = {
    init: function () {
      var selects = $$('.CurrencySelector__Select');
      selects.forEach(function (sel) {
        on(sel, 'change', function () {
          document.cookie = 'cart_currency=' + sel.value + '; path=/; max-age=31536000';
          location.reload();
        });
      });
    }
  };

  /* ================================================================
     FEATURE DETECTION CLASSES (Prestige expects these on <html>)
     ================================================================ */
  var FeatureDetection = {
    init: function () {
      var root = document.documentElement;
      root.classList.remove('no-js');
      root.classList.add('js');
      var sticky = (window.CSS && window.CSS.supports && window.CSS.supports('position', 'sticky'));
      root.classList.add(sticky ? 'supports-sticky' : 'no-supports-sticky');
      var hover = window.matchMedia('(hover: hover)').matches;
      root.classList.add(hover ? 'supports-hover' : 'no-supports-hover');
      root.style.setProperty('--window-height', window.innerHeight + 'px');
    }
  };

  /* ================================================================
     BOOT
     ================================================================ */
  function boot() {
    FeatureDetection.init();
    PageOverlay.init();
    DrawerManager.init();
    ModalManager.init();
    Collapsible.init();
    HeaderScroll.init();
    MegaMenuKeyboard.init();
    HeaderHeight.init();
    CurrencySelector.init();
  }

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify section reload (theme editor)
  on(document, 'shopify:section:load', function (e) {
    var sec = e.target;
    // Re-init only the components that may have new DOM
    HeaderScroll.init();
    HeaderHeight.init();
    MegaMenuKeyboard.init();
    CurrencySelector.init();
  });

  // Expose globally for the cart bridge / debugging
  window.PdaPrestige = {
    DrawerManager: DrawerManager,
    ModalManager: ModalManager,
    Collapsible: Collapsible,
    HeaderScroll: HeaderScroll,
    HeaderHeight: HeaderHeight
  };
})();
